<template lang="pug">
.bq-service-layout(v-if="currentService")
    aside.bq-sidebar
        SideNav

    main.bq-main
        //- banners & warnings (shared across all service pages)
        EmailVerifyBanner

        .bq-warning(v-if="!currentService.service?.will_terminate && currentService.subscription?.will_cancel")
            span.material-symbols-outlined.bq-banner-icon(translate="no") warning
            span Subscription cancels on {{ currentService.subscription?.will_cancel }}.
            |
            router-link.bq-link(:to="`/subscription/${currentService.id}`") [Manage]

        .bq-warning.bq-warning--danger(v-if="currentService.service?.will_terminate")
            span.material-symbols-outlined.bq-banner-icon(translate="no") priority_high
            span Service deletes on {{ currentService.service?.will_terminate }}.
            |
            router-link.bq-link(:to="`/subscription/${currentService.id}`") [Resume plan]

        .bq-warning.bq-warning--danger(v-if="currentService.subscription?.status === 'past_due'" @click="openBillingPage" style="cursor:pointer")
            span.material-symbols-outlined.bq-banner-icon(translate="no") priority_high
            span Suspended due to payment issue. Click to manage billing.

        router-view

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { onMounted, ref, watch } from "vue";
import { user, customer } from "@/code/user";
import { skapi } from "@/main";
import { fullScreenLoader } from "@/components/jsComponents";
import {
    currentService,
} from "@/views/project-list";
import {
    serviceIdList,
    serviceList,
    pullServices,
    recordsRefreshTrigger,
    prefetchAiApiKey,
} from "@/views/project-list";
import { bgTaskQueue, agentViewMounted, POLL_INTERVAL } from "@/code/ai_agent";

import SideNav from "@/components/sideNav.vue";
import Modal from "@/components/modal.vue";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";

const router = useRouter();
const route = useRoute();

let serviceId = route.path.split("/")[2];

// Fallback completion-poller for background indexing tasks. agent.vue owns
// polling + chat rendering of bgTaskQueue while it's open; when it's closed
// (user on the db-files or records tab) nothing else advances the queue, so
// indexing completion — and the records.vue auto-refresh it triggers — would
// never fire. While agentViewMounted is false this polls pending tasks, removes
// them when done, and bumps recordsRefreshTrigger when a service's queue drains.
// (`poll` is safe to call repeatedly; an all-cancelled batch just causes a
// harmless extra refresh.)
const bgFallbackPolls = new Set<string>();
const finishBgFallbackTask = (id: string, svc: string) => {
    bgFallbackPolls.delete(id);
    const i = bgTaskQueue.findIndex((e) => e.id === id);
    if (i !== -1) bgTaskQueue.splice(i, 1);
    if (!bgTaskQueue.some((e) => e.serviceId === svc)) {
        recordsRefreshTrigger[svc] = (recordsRefreshTrigger[svc] || 0) + 1;
    }
};
watch(
    () => (agentViewMounted.value ? "" : bgTaskQueue.map((e) => e.id).join(",")),
    () => {
        if (agentViewMounted.value) return;
        for (const entry of bgTaskQueue) {
            if (bgFallbackPolls.has(entry.id) || !entry.poll) continue;
            bgFallbackPolls.add(entry.id);
            const id = entry.id;
            const svc = entry.serviceId;
            entry.poll({ latency: POLL_INTERVAL })
                .then(() => finishBgFallbackTask(id, svc))
                .catch(() => finishBgFallbackTask(id, svc));
        }
    },
    { immediate: true }
);

async function run() {
    currentService.value = null;

    let isAwait = false;
    if (serviceId.startsWith("await-")) {
        isAwait = true;
        serviceId = serviceId.replace("await-", "");
    }

    let serviceInst = serviceList?.[serviceId];
    if (!serviceInst) {
        serviceInst = pullServices({ service_id_to_fetch: serviceId });
    }

    if (serviceInst instanceof Promise) {
        serviceInst = await serviceInst;
        if (isAwait) {
            router.push(`/my-projects/${serviceId}`);
        }
    }

    if (!serviceInst) {
        router.push('/my-projects');
        return;
    }

    currentService.value = serviceInst;

    // Kick off the AI API-key fetch FIRST — the moment the service is known — so
    // it's the earliest request on project open (ahead of subdomain info) and is
    // already warm by the time the agent view mounts. Deduped + non-blocking;
    // refreshGate reuses this in-flight request instead of firing its own.
    void prefetchAiApiKey(currentService.value);

    // // Subdomain info (only the service views need it) — on VISIT, but only ONCE
    // // per service: subdInfo.srvc is set the first time a fetch settles, and the
    // // Service instance is cached across visits, so skip the re-fetch on re-entry.
    // // (The hosting view still re-fetches via getInfo() after an actual subdomain
    // // change; the internal poll's own timer guard covers in-progress fetches.)
    // if (!currentService.value?.subdInfo?.srvc) {
    //     currentService.value?.getSubdomainInfo?.();
    // }
}
run();

let openBillingPage = async () => {
    fullScreenLoader();
    let resolvedCustomer = await customer;
    skapi
        .clientSecretRequest({
            clientSecretName: "stripe_test",
            url: `https://api.stripe.com/v1/billing_portal/sessions`,
            method: "POST",
            headers: {
                Authorization: "Bearer $CLIENT_SECRET",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data: {
                customer: resolvedCustomer.id,
                return_url: window.location.origin + route.path,
            },
        })
        .then((response) => {
            window.location = response.url;
        });
};
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-service-layout {
    display: flex;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    background: @paper;
    color: @ink;
    font-family: @mono;
}

.bq-sidebar {
    position: sticky;
    top: calc(4rem + var(--nav-top));
    width: 200px;
    flex-shrink: 0;
    overflow-y: auto;
    align-self: flex-start;
}

.bq-main {
    flex: 1;
    padding: 3rem 3rem 3rem;
    min-width: 0;
}

/* --- warning --- */
.bq-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border: 1px solid @warning-border;
    background: @warning-bg;
    color: @warning;
    font-size: 0.82rem;
    margin-bottom: 0.5rem;
    line-height: 1.5;

    &--danger {
        border-color: @danger;
        background: @danger-bg;
        color: @danger;
    }
}

.bq-link {
    &:hover {
        text-decoration: underline;
    }
}

@media (max-width: 800px) {
    .bq-sidebar {
        display: none;
    }

    .bq-main {
        padding: 1rem;
    }
}
</style>
